// HarmonogramTab — Gantt harmonogram zákazky (ústava: HARMONOGRAM PRE-FLIGHT 2026-06-10)
// m² ŽIVÉ z Výmer (project_id, GROUP BY obj+po). Poschodia s 0 m² sa nerátajú (1.NP).
// Výkon PER PARTIA (m²/týždeň) — VERÍME VÝKONU (Mirko 2026-06-10, žiadny strop).
// 2 partie na poschodí = súčet výkonov. Práca Po–Pia; víkend = prepínač per partia.
// Partia môže byť priradená OBJEKTU — Auto-naplánovať reťazí partie v ich objekte.
// Zadávanie: klik do mriežky (týždeň × poschodie) → popover s partiami (ako Rozpis).
// Dovolenka partie = medzera (šrafovaný úsek). Hotové poschodia = zelené ✓, rátajú sa do %.
// SPEC harmonogram v1 (15. 9. 2026): toto je režim „Výkon“ jedného remesla; prepínač remesla a režimu je HarmonogramTab na konci súboru.
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchAuth, fetchAuthJSON } from '../auth/fetchAuth.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { Btn, BtnAdd, BtnDanger, BtnGhost } from '../components/Btn.jsx'
import { Modal } from '../components/Modal.jsx'
import { useToast } from '../components/Toast.jsx'
import { Tabs } from '../components/Ui.jsx'
import ErrorBoundary from '../components/ErrorBoundary.jsx'
import { COLORS, TYPO, SPACE, RADIUS } from '../theme.js'
import useLiveRefresh from '../hooks/useLiveRefresh.js'
import { setDirty, confirmLeave, isAnyDirty } from '../hooks/unsavedGuard.js'
import { REMESLA } from '../utils/harmonogramHarok.js'
import HarmonogramHarok from './HarmonogramHarok.jsx'
// HARMONOGRAM_VYKON_V1 (26. 10. 2026): výpočty plánu sú v spoločnom súbore — tie isté používa Rozpis › Stavba › Harmonogram
import { PALETTE, DEFAULT_VYKON, addDays, fmtD, fmtM2, mondayOf, nextWork, ranges, vypocitajPlan, segOf, navrhAutoPlan } from '../utils/harmonogramVykon.js'

const WRITE_ROLES = ['admin', 'owner', 'office_manager', 'owner_view']

// Server-side render HTML → .pdf (puppeteer, @page A4) — rovnaký vzor ako SupisTab (ústava 67)
async function downloadPdfFile(html, filename, onErr) {
  try {
    const r = await fetchAuth('/api/render-pdf', { method: 'POST', body: JSON.stringify({ html, filename }) })
    if (!r.ok) throw new Error('render ' + r.status)
    const blob = await r.blob()
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename.endsWith('.pdf') ? filename : filename + '.pdf'
    document.body.appendChild(a); a.click(); setTimeout(() => { URL.revokeObjectURL(a.href); a.remove() }, 5000)
  } catch (e) {
    const w = window.open('', '_blank'); if (w) { w.document.write(html); w.document.close() } else if (onErr) onErr('PDF zlyhalo: ' + e.message)
  }
}

const card = { background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }
const inputStyle = {
  padding: '7px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md,
  fontSize: TYPO.sizes.base, fontFamily: TYPO.family, color: COLORS.text, background: COLORS.bg, outline: 'none'
}
const lbl = { fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, display: 'block' }
const hintStyle = { fontSize: 11, color: COLORS.muted, marginTop: 3, lineHeight: 1.4 }

function Kpi({ label, value, sub, color }) {
  return (
    <div style={{ ...card, borderLeft: `4px solid ${color}`, padding: `${SPACE.md}px ${SPACE.lg}px`, flex: '1 1 150px', minWidth: 150 }}>
      <div style={lbl}>{label}</div>
      <div style={{ fontSize: TYPO.sizes.xl, fontWeight: TYPO.weights.extra, color: COLORS.base, lineHeight: 1.15 }}>{value}</div>
      {sub ? <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 2 }}>{sub}</div> : null}
    </div>
  )
}

function HarmonogramVykon({ projectId, remeslo = 'omietky' }) {
  const toast = useToast()
  const navigate = useNavigate()
  const { user } = useAuth()
  // omietky = presne pôvodné URL bez parametrov; ostatné remeslá cez query
  const url = remeslo === 'omietky' ? `/api/harmonogram/${projectId}` : `/api/harmonogram/${projectId}?remeslo=${encodeURIComponent(remeslo)}&cast=vykon`
  // starý server remeslo nepozná a zápis iného remesla by prepísal plán omietok — vtedy len čítanie
  const [serverOk, setServerOk] = useState(remeslo === 'omietky')
  // HARMONOGRAM_SUBEH_V1 (Mirko 26. 10. 2026: „daj to do poriadku“): ukladanie posiela verziu (base_rev) ako Rozpis › Stavba a hárok —
  // keď harmonogram medzitým uložil niekto iný (409), nič sa neprepíše, ukáže sa kto a ponúkne sa načítať jeho verziu
  const [konflikt, setKonflikt] = useState(null)   // { kto, kedy }
  const revRef = useRef(null)        // verzia plánu zo servera, z ktorej vychádzam
  const zakladRef = useRef(null)     // plán zo servera (JSON), z ktorého vychádzam — pre opakovanie pri zmene len háreku
  const konfliktRef = useRef(null)
  const canEdit = WRITE_ROLES.includes(user?.role) && serverOk && !konflikt
  const remesloText = ((REMESLA.find(r => r.key === remeslo) || {}).label || 'omietky').toLowerCase()
  const [floors, setFloors] = useState([])
  const [rozpis, setRozpis] = useState([])
  const [aktivita, setAktivita] = useState([])
  const [plan, setPlan] = useState(null)
  const [loading, setLoading] = useState(true)
  const [savedAt, setSavedAt] = useState(null)
  const [pdfBusy, setPdfBusy] = useState(false)
  const [editFloor, setEditFloor] = useState(null)   // {obj,po,m2}
  const [editParty, setEditParty] = useState(null)   // party object alebo {} pre novú
  const [popover, setPopover] = useState(null)       // {key,obj,po,m2,week,x,y} — klik do mriežky
  const saveTimer = useRef(null)
  const dirtyRef = useRef(false)   // true kým má plán neuložené zmeny → live refresh ho neprepíše
  const pendingRef = useRef(null)  // {url, body, seq} — URL zachytené pri zmene, nie až pri odoslaní
  const seqRef = useRef(0)
  const chainRef = useRef(Promise.resolve())
  const hlasenyRef = useRef(false) // v unsavedGuard len zápis, ktorý zlyhal — čakajúci autosave sa pri odchode odošle sám
  const dirtyKey = `harmonogram:vykon:${projectId}:${remeslo}`

  // ── load ──
  useEffect(() => {
    let alive = true
    setLoading(true)
    fetchAuthJSON(url)
      .then(d => { if (!alive) return
        if (remeslo !== 'omietky') setServerOk(!!d && d.remeslo === remeslo)
        setFloors(d.floors || [])
        setRozpis(d.rozpis || [])
        setAktivita(d.aktivita || [])
        setPlan(d.plan && typeof d.plan === 'object' ? { parties: [], floors: {}, ...d.plan } : { deadline: '', parties: [], floors: {} })
        revRef.current = d.rev != null ? d.rev : null; zakladRef.current = JSON.stringify(d.plan == null ? null : d.plan)
      })
      .catch(e => toast.error('Harmonogram sa nepodarilo načítať: ' + e.message))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [url])

  // ── autosave (debounce 800ms); zápisy idú za sebou, čakajúci sa pri odchode odošle hneď ──
  const sendPending = useCallback(() => {
    const job = pendingRef.current
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null }
    if (!job) return chainRef.current
    pendingRef.current = null
    chainRef.current = chainRef.current.catch(() => {}).then(async () => {
      if (konfliktRef.current) { if (!pendingRef.current) pendingRef.current = job; return }
      const json = JSON.stringify(job.body)
      const put = () => fetchAuth(job.url + (revRef.current != null ? (job.url.includes('?') ? '&' : '?') + 'base_rev=' + revRef.current : ''), { method: 'PUT', body: json })
      let r = await put()
      if (r.status === 409) {
        // verzia je spoločná pre výkon aj hárok remesla: keď sa zmenil len hárok a výkon je na serveri stále ten, z ktorého vychádzam, zápis sa raz zopakuje
        let g = null
        try { g = await fetchAuthJSON(url) } catch (_) {}
        if (g && g.rev != null && JSON.stringify(g.plan == null ? null : g.plan) === zakladRef.current) { revRef.current = g.rev; r = await put() }
      }
      if (r.status === 409) {
        let d = {}
        try { d = await r.json() } catch (_) {}
        konfliktRef.current = { kto: d.updated_by || '', kedy: d.updated_at || '' }
        if (!pendingRef.current) pendingRef.current = job
        setKonflikt(konfliktRef.current)
        toast.warn(`Harmonogram medzitým uložil ${d.updated_by || 'iný používateľ'} — tvoja zmena sa neuložila. Načítaj jeho verziu.`)
        return
      }
      if (!r.ok) throw new Error('HTTP ' + r.status)
      try { const j = await r.json(); if (j && j.rev != null) revRef.current = j.rev } catch (_) {}
      zakladRef.current = json
      const d = new Date()
      setSavedAt(String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0'))
      // novší časovač alebo čakajúci zápis = plán je stále rozpísaný
      if (seqRef.current === job.seq && !pendingRef.current) {
        dirtyRef.current = false
        if (hlasenyRef.current) { hlasenyRef.current = false; setDirty(job.dirtyKey, false) }
      }
    }).catch(e => {
      if (!pendingRef.current) pendingRef.current = job
      hlasenyRef.current = true
      setDirty(job.dirtyKey, true, () => sendRef.current())
      toast.error('Uloženie harmonogramu zlyhalo: ' + e.message)
      throw e
    })
    return chainRef.current
  }, [])
  const sendRef = useRef(sendPending)
  sendRef.current = sendPending

  // opts.dovod (autoplan): zápis ide hneď a server pred ním odloží snímku plánu do histórie; starý server query ignoruje
  const persist = useCallback((next, opts) => {
    setPlan(next)
    if (!canEdit) return
    const dovod = opts && opts.dovod
    if (dovod) sendRef.current().catch(() => {})
    pendingRef.current = { url: dovod ? url + (url.includes('?') ? '&' : '?') + 'dovod=' + encodeURIComponent(dovod) : url, body: next, seq: ++seqRef.current, dirtyKey }
    dirtyRef.current = true
    if (saveTimer.current) clearTimeout(saveTimer.current)
    if (dovod) { saveTimer.current = null; sendRef.current().catch(() => {}); return }
    saveTimer.current = setTimeout(() => { saveTimer.current = null; sendRef.current().catch(() => {}) }, 800)
  }, [url, canEdit, dirtyKey])

  useEffect(() => () => {
    const key = dirtyKey
    // Zahodiť v dialógu neuložených zmien vyprázdni unsavedGuard — vtedy sa neuložený zápis neopakuje
    if (hlasenyRef.current && !isAnyDirty()) {
      pendingRef.current = null
      if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null }
      return
    }
    sendRef.current().then(() => { if (!pendingRef.current) setDirty(key, false) }).catch(() => {})
  }, [dirtyKey])

  // živé obnovenie (SSE) — re-fetch floors/rozpis/aktivita + plán, ALE len keď needituješ a nemáš neuložené zmeny
  const nacitajZnova = useCallback(() => fetchAuthJSON(url).then(d => {
    setFloors(d.floors || []); setRozpis(d.rozpis || []); setAktivita(d.aktivita || [])
    setPlan(d.plan && typeof d.plan === 'object' ? { parties: [], floors: {}, ...d.plan } : { deadline: '', parties: [], floors: {} })
    revRef.current = d.rev != null ? d.rev : null; zakladRef.current = JSON.stringify(d.plan == null ? null : d.plan)
  }), [url])
  useLiveRefresh(() => { nacitajZnova().catch(() => {}) }, () => !dirtyRef.current && !konfliktRef.current && !editFloor && !editParty && !popover && !pdfBusy)
  // po súbehu: zahodí moje neuložené zmeny a načíta verziu, ktorú uložil iný
  const prevezmiVerziu = () => {
    pendingRef.current = null
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null }
    nacitajZnova().then(() => {
      konfliktRef.current = null; setKonflikt(null); dirtyRef.current = false
      if (hlasenyRef.current) { hlasenyRef.current = false; setDirty(dirtyKey, false) }
    }).catch(e => toast.error('Harmonogram sa nepodarilo načítať: ' + e.message))
  }

  // ── odvodené dáta ──
  const data = useMemo(() => vypocitajPlan(plan, floors), [plan, floors])

  if (loading || !plan) return <div style={{ padding: SPACE.xl, color: COLORS.muted }}>Načítavam harmonogram…</div>

  if (!floors.length) return (
    <div style={{ ...card, padding: SPACE.xl, margin: SPACE.lg, textAlign: 'center', color: COLORS.muted }}>
      <div style={{ fontSize: TYPO.sizes.md, fontWeight: TYPO.weights.semibold, color: COLORS.text }}>Žiadne výmery</div>
      <div style={{ marginTop: 6, fontSize: TYPO.sizes.base }}>Harmonogram ťahá m² automaticky z karty Výmery. Najprv zadaj výmery (objekt + poschodia), potom sa tu zobrazia.</div>
    </div>
  )

  const parties = plan.parties || []
  const D = data

  // ── mutácie ──
  const setFloorCfg = (key, patch) => {
    const next = { ...plan, floors: { ...(plan.floors || {}), [key]: { ...((plan.floors || {})[key] || {}), ...patch } } }
    persist(next)
  }
  const saveParty = p => {
    const list = parties.some(x => x.id === p.id) ? parties.map(x => (x.id === p.id ? p : x)) : [...parties, p]
    persist({ ...plan, parties: list })
    setEditParty(null)
  }
  const removeParty = id => {
    const fl2 = {}
    for (const [k, v] of Object.entries(plan.floors || {})) fl2[k] = { ...v, parties: (v.parties || []).filter(x => x !== id) }
    persist({ ...plan, parties: parties.filter(p => p.id !== id), floors: fl2 })
    setEditParty(null)
  }
  const clearFloor = key => {
    const fl2 = { ...(plan.floors || {}) }
    delete fl2[key]
    persist({ ...plan, floors: fl2 })
  }

  // ── AUTO-NAPLÁNOVAŤ (Mirko 2026-06-10): výpočet je v utils/harmonogramVykon.js (navrhAutoPlan), tu len otázka a uloženie
  const autoPlan = () => {
    const n = navrhAutoPlan(plan, floors, aktivita)
    if (n.prazdne) { toast.info('Niet čo plánovať — všetky poschodia sú hotové alebo priradené.'); return }
    if (!window.confirm(`Auto-naplánovať ${n.poschodi} nepriradených poschodí?\n\n1. Podľa VÝMER — kto kde reálne pracuje (pole Parťák pri bytoch); hotové byty sa odrátajú.\n2. Zvyšok reťazením — partie zdvojené (2 na poschodie), sólo partie samé.\n\nHotové a ručne priradené poschodia sa NEMENIA.`)) return
    if (n.bezPartii) { toast.warn('Žiadne partie — pridaj partie (+ Partia) alebo vyplň pole Parťák pri bytoch vo Výmerách.'); return }
    persist(n.plan, { dovod: 'autoplan' })
    toast.success(`Navrhnuté: ${n.zVymer} poschodí podľa Výmer (kto kde pracuje) + ${n.retazenych} reťazením. Koriguj klikom do mriežky.`)
  }

  // ── EXPORT PDF — Matrix štandard A4 (ústava 67, vzor SupisTab fakturácia) ──
  const exportPdf = async () => {
    if (pdfBusy) return
    setPdfBusy(true)
    try {
    let proj = null
    try { const p = await fetchAuthJSON('/api/projects/' + projectId); proj = p && p.project ? p.project : p } catch (e) { proj = null }
    let cust = null
    try {
      if (proj && proj.customerId) { const c = await fetchAuthJSON('/api/customers/' + proj.customerId); cust = c && c.customer ? c.customer : c }
    } catch (e) { cust = null }
    const esc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
    const now = new Date()
    const todayStr = now.getDate() + '. ' + (now.getMonth() + 1) + '. ' + now.getFullYear()
    const pctH = D.total ? Math.round(D.doneTotal / D.total * 100) : 0
    const dlLine = plan.deadline ? `<div class="dl" style="left:${D.pos(plan.deadline)}%"></div>` : ''
    const tdLine = `<div class="tdl" style="left:${D.pos(D.today)}%"></div>`
    const weekTicks = D.weeks.map(w => `<div class="wt" style="left:${D.pos(w)}%">${fmtD(w)}</div>`).join('')
    let body = ''
    for (const [obj, rows] of Object.entries(D.objs)) {
      body += `<tr class="sec"><td class="l" colspan="3">OBJEKT ${esc(obj)} — Σ ${fmtM2(rows.reduce((s, f) => s + f.m2, 0))} m²</td><td class="gc"><div class="g gs">${dlLine}${tdLine}</div></td></tr>`
      for (const f of rows) {
        const ps = (f.cfg.parties || []).map(id => parties.find(p => p.id === id)).filter(Boolean)
        let lines = '', bars = ''
        if (f.cfg.done) {
          lines = `<span class="ok">✓ HOTOVÉ</span>${ps.length ? ' · ' + ps.map(p => esc(p.name)).join(' + ') : ''}${f.cfg.doneFrom && f.cfg.doneTo ? ` <span class="dt">${fmtD(f.cfg.doneFrom)} – ${fmtD(f.cfg.doneTo)}</span>` : ''}`
          if (f.cfg.doneFrom && f.cfg.doneTo && f.cfg.doneTo >= f.cfg.doneFrom)
            bars = `<div class="bar bdone" style="left:${D.pos(f.cfg.doneFrom)}%;width:${Math.max(1, D.pos(addDays(f.cfg.doneTo, 1)) - D.pos(f.cfg.doneFrom))}%;top:2px;height:12px"></div>`
        } else if (f.sim) {
          const n = ps.length
          lines = ps.map(p => {
            const s = segOf(f, p)
            return `<div class="pl"><span class="pd" style="background:${p.color}"></span>${esc(p.name)} <span class="dt">${s ? fmtD(s.st) + ' – ' + fmtD(s.en) : ''}</span></div>`
          }).join('') || `<div class="pl mut">bez partie <span class="dt">${fmtD(f.cfg.start || f.sim.end)} – ${fmtD(f.sim.end)}</span></div>`
          if (f.doneM2 > 0) lines += `<div class="ok">${Math.round(f.doneM2 / f.m2 * 100)} % hotové</div>`
          const h = n > 1 ? Math.max(5, Math.floor(13 / n)) : 12
          bars = ps.map((p, i) => {
            const s = segOf(f, p)
            if (!s) return ''
            return `<div class="bar" style="left:${D.pos(s.st)}%;width:${Math.max(1, D.pos(addDays(s.en, 1)) - D.pos(s.st))}%;top:${2 + i * (h + 1)}px;height:${h}px;background:${p.color}"></div>`
          }).join('')
          if (!n) bars = `<div class="bar" style="left:${D.pos(f.cfg.start || f.sim.end)}%;width:${Math.max(1, D.pos(addDays(f.sim.end, 1)) - D.pos(f.cfg.start || f.sim.end))}%;top:2px;height:12px;background:#9ca3af"></div>`
          bars += ranges(f.sim.vacDays).map(r => `<div class="vac" style="left:${D.pos(r.from)}%;width:${Math.max(0.6, D.pos(addDays(r.to, 1)) - D.pos(r.from))}%"></div>`).join('')
        } else {
          lines = '<span class="mut">nepriradené</span>'
        }
        body += `<tr><td class="l po"><b>${esc(f.po)}</b></td><td class="vym">${fmtM2(f.m2)} m² · ${f.byty} b.</td><td class="l">${lines}</td><td class="gc"><div class="g">${bars}${dlLine}${tdLine}</div></td></tr>`
      }
    }
    const html = `<!DOCTYPE html><html lang="sk"><head><meta charset="utf-8"><title>Harmonogram ${esc(projectId)}</title>
<style>
:root{--brand:#ef9350;--brand-d:#d97a3a;--green:#15803d;--line:#d8d2c8;--ink:#1f2937;--muted:#6b6359;--mono:'SF Mono',ui-monospace,Menlo,monospace}
*{box-sizing:border-box}
body{font-family:-apple-system,system-ui,'Segoe UI',Arial,sans-serif;margin:0;background:#fff;color:var(--ink);font-size:12px}
.page{max-width:100%;margin:0 auto;background:#fff;padding:8mm 12mm}
.top{display:flex;align-items:flex-start;gap:16px;border-bottom:3px solid var(--brand);padding-bottom:8px}
.logo img{height:40px;width:auto;display:block}
.ttl{flex:1;text-align:center}.ttl h1{margin:0;font-size:20px;letter-spacing:1px}
.ttl .sub{font-size:13px;color:var(--brand-d);font-weight:700;margin-top:3px}
.ttl .obd{font-family:var(--mono);font-size:12px;color:var(--muted);margin-top:2px}
.docno{flex-shrink:0;text-align:right;font-size:11px;color:var(--muted)}.docno b{color:var(--ink);font-family:var(--mono)}
.meta{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin:8px 0 6px}
.mbox{border:1px solid var(--line);border-radius:8px;padding:6px 12px}
.mbox .ml{font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:var(--muted);font-weight:700}
.mbox .mv{font-weight:700;font-size:13px}.mbox .mi{font-size:11px;color:var(--muted);font-family:var(--mono)}
table{width:100%;border-collapse:collapse;font-size:10px}
thead th{border:1px solid var(--line);padding:4px 5px;text-align:center;font-size:8.5px;text-transform:uppercase;color:#fff;background:#7a7268;font-weight:700}
th.l,td.l{text-align:left}
tbody td{border:1px solid #eee7dc;padding:3px 5px;text-align:center;font-family:var(--mono)}
td.l{text-align:left;font-family:inherit}
tbody tr{page-break-inside:avoid}
tr.sec{page-break-after:avoid}
tr.sec td{background:#fdf3ea;font-weight:800;color:var(--brand-d);font-size:10.5px;padding:4px 5px}
.po{width:46px}.vym{width:88px;white-space:nowrap;font-size:9px}
.pl{white-space:nowrap;line-height:1.5}.dt{font-family:var(--mono);color:var(--muted);font-size:9px}
.pd{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:4px;vertical-align:0}
.ok{color:var(--green);font-weight:800;font-size:9.5px}.mut{color:#9ca3af}
.gc{width:62%;padding:2px 4px}.g{position:relative;height:16px;background:#f4f1ec;border-radius:3px;overflow:hidden}.g.gs{height:10px}
.bar{position:absolute;top:2px;height:12px;border-radius:3px}.bar.bdone{background:#bbe3c5;border:1px solid var(--green)}
.vac{position:absolute;top:2px;height:12px;background:repeating-linear-gradient(45deg,rgba(255,255,255,.85),rgba(255,255,255,.85) 3px,transparent 3px,transparent 6px)}
.dl{position:absolute;top:0;bottom:0;width:2px;background:#b91c1c}.tdl{position:absolute;top:0;bottom:0;width:1px;background:#2563eb;opacity:.55}
.tline{position:relative;height:12px;margin:0}.wt{position:absolute;font-size:7.5px;color:#fff;font-family:var(--mono);transform:translateX(2px)}
.foot{margin-top:8px;border-top:1px solid var(--line);padding-top:6px;font-size:9px;color:var(--muted);display:flex;justify-content:space-between;gap:10px;white-space:nowrap;page-break-inside:avoid}
@page{size:A4 landscape;margin:0}
</style></head><body><div class="page">
<div class="top"><div class="logo"><img src="/uploads/kamir_logo.png" alt="KAMIR"></div>
<div class="ttl"><h1>HARMONOGRAM PRÁC</h1><div class="sub">Plán nasadenia partií — ${esc(remesloText)}</div><div class="obd">Stav k ${todayStr}${plan.deadline ? ` · Termín: ${fmtD(plan.deadline)}${plan.deadline.slice(0, 4)}` : ''} · Výmera ${fmtM2(D.total)} m² · Hotovo ${pctH} % · Predpoklad: ${D.predpoklad ? fmtD(D.predpoklad) + D.predpoklad.slice(0, 4) : '—'} · Rezerva ${D.rezerva == null ? '—' : (D.rezerva >= 0 ? '+' : '') + D.rezerva + ' dní'}</div></div>
<div class="docno">Zákazka<br><b>${esc(projectId)}</b><br>List 1/1</div></div>
<div class="meta"><div class="mbox"><div class="ml">Objednávateľ</div><div class="mv">${esc(cust?.name || proj?.customer_name || '—')}</div><div class="mi">${cust ? [cust.ico ? 'IČO ' + esc(cust.ico) : '', cust.ic_dph ? 'IČ DPH ' + esc(cust.ic_dph) : (cust.dic ? 'DIČ ' + esc(cust.dic) : '')].filter(Boolean).join(' · ') : ''}</div><div class="mi">Stavba: ${esc(proj?.name || '')}</div></div>
<div class="mbox"><div class="ml">Zhotoviteľ</div><div class="mv">KAMIR s.r.o.</div><div class="mi">IČO 48029882 · DIČ 2120047358</div></div></div>
<table><thead><tr><th class="l po">Poschodie</th><th class="vym">Výmera</th><th class="l">Partia · termín</th><th class="gc"><div class="tline">${weekTicks}</div></th></tr></thead><tbody>${body}</tbody></table>
<div class="foot"><span>KAMIR s.r.o. · IČO 48029882 · Prešov · kamir.sk</span><span>Červená čiara = termín · modrá = dnes · šrafovanie = dovolenka</span><span>Vygenerované v systéme MATRIX · ${todayStr}</span></div>
</div></body></html>`
    await downloadPdfFile(html, `Harmonogram-${projectId}${remeslo !== 'omietky' ? '-' + remeslo : ''}-${D.today}`, m => toast.error(m))
    } finally { setPdfBusy(false) }
  }

  const deadlinePos = plan.deadline && plan.deadline >= D.min && plan.deadline <= D.max ? D.pos(plan.deadline) : null
  const todayPos = D.today >= D.min && D.today <= D.max ? D.pos(D.today) : null
  const pct = D.total ? Math.round(D.doneTotal / D.total * 100) : 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.lg, padding: `${SPACE.md}px 0` }}>
      {konflikt && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: SPACE.md, flexWrap: 'wrap', padding: '8px 12px', background: '#fef2f2', border: `1px solid ${COLORS.remove}`, borderRadius: RADIUS.md, color: COLORS.remove, fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold }}>
          <span style={{ flex: 1 }}>Harmonogram medzitým uložil {konflikt.kto || 'iný používateľ'}{konflikt.kedy ? ' (' + String(konflikt.kedy).slice(11, 16) + ')' : ''} — napríklad v Rozpise › Stavba. Tvoja posledná zmena sa neuložila, aby sa jeho neprepísala.</span>
          <Btn size="sm" variant="nav" onClick={prevezmiVerziu}>Načítať jeho verziu</Btn>
        </div>
      )}

      {/* ── horná lišta: termín + partie + uložené ── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: SPACE.lg, flexWrap: 'wrap' }}>
        <div>
          <span style={lbl}>Začiatok</span>
          <input type="date" value={plan.start || ''} disabled={!canEdit}
            onChange={e => persist({ ...plan, start: e.target.value })} style={inputStyle} />
        </div>
        <div>
          <span style={lbl}>Termín dokončenia</span>
          <input type="date" value={plan.deadline || ''} disabled={!canEdit}
            onChange={e => persist({ ...plan, deadline: e.target.value })} style={inputStyle} />
        </div>
        {canEdit && <BtnAdd onClick={() => setEditParty({ id: 'pt' + Math.random().toString(36).slice(2, 8), name: '', color: PALETTE[parties.length % PALETTE.length], vykon: DEFAULT_VYKON, weekend: false, solo: false, objekt: '', dovolenky: [] })}>+ Partia</BtnAdd>}
        {canEdit && <Btn variant="nav" onClick={autoPlan}>Auto-naplánovať</Btn>}
        {remeslo === 'omietky' && new Set((floors || []).filter(f => f.m2 > 0).map(f => f.obj + '|' + f.po)).size > 1 && <BtnGhost onClick={() => confirmLeave().then(ok => { if (ok) navigate('/rozpis/stavba/' + encodeURIComponent(projectId) + '?pohlad=harmonogram') })} title="ten istý harmonogram v Rozpise prác › Stavba (po dňoch, s tým, kde sú partie v Rozpise)">Otvoriť v Rozpise</BtnGhost>}
        <BtnGhost onClick={exportPdf} disabled={pdfBusy} style={pdfBusy ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>{pdfBusy ? 'Generujem PDF…' : 'PDF'}</BtnGhost>
        <div style={{ flex: 1 }} />
        {savedAt && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>Uložené {savedAt}</span>}
      </div>
      <div style={{ ...hintStyle, marginTop: -8 }}>
        m² sa ťahajú živé z karty Výmery (poschodia s 0 m² sa nerátajú). Auto-naplánovať najprv číta VÝMERY — kto kde reálne pracuje (pole Parťák pri bytoch) a hotové byty odráta; zvyšok reťazí so ZDVOJENÝMI partiami (sólo partia robí sama — nastav v partii). Koriguj KLIKOM do mriežky na týždeň/poschodie. Výkon partie = m²/týždeň (veríme výkonu), práca Po–Pia — víkend len ak má partia „robí víkend". Dve partie na poschodí = súčet výkonov.
      </div>

      {/* ── KPI ── */}
      <div style={{ display: 'flex', gap: SPACE.md, flexWrap: 'wrap' }}>
        <Kpi label="Výmera spolu" value={fmtM2(D.total) + ' m²'} sub={`${D.rows.length} poschodí z Výmer`} color={COLORS.total} />
        <Kpi label="Hotovo" value={pct + ' %'} sub={fmtM2(D.doneTotal) + ' m² hotových'} color={COLORS.add} />
        <Kpi label="Predpoklad dokončenia" value={D.predpoklad ? fmtD(D.predpoklad) : '—'} sub={D.unassigned ? `${D.unassigned} poschodí nepriradených` : 'všetko naplánované'} color={COLORS.nav} />
        <Kpi label="Rezerva do termínu" value={D.rezerva == null ? '—' : (D.rezerva >= 0 ? '+' : '') + D.rezerva + ' dní'} sub={plan.deadline ? 'termín ' + fmtD(plan.deadline) : 'termín nezadaný'} color={D.rezerva != null && D.rezerva < 0 ? COLORS.remove : COLORS.add} />
      </div>

      {/* ── legenda partií ── */}
      {parties.length > 0 && (
        <div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap', alignItems: 'center' }}>
          {parties.map(p => (
            <button key={p.id} onClick={() => canEdit && setEditParty({ dovolenky: [], ...p })}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', border: `1px solid ${COLORS.line}`, borderRadius: 999, background: COLORS.bg, cursor: canEdit ? 'pointer' : 'default', fontSize: TYPO.sizes.sm, fontFamily: TYPO.family, color: COLORS.text }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
              <b>{p.name || '(bez mena)'}</b>
              {p.objekt && <span style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.nav, background: COLORS.navBg, padding: '1px 6px', borderRadius: 4 }}>{p.objekt}</span>}
              <span style={{ color: COLORS.muted }}>{Number(p.vykon) || DEFAULT_VYKON} m²/týž</span>
              {p.weekend && <span style={{ color: COLORS.warn, fontSize: TYPO.sizes.xs }}>víkend</span>}
              {p.solo && <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.xs }}>sólo</span>}
              {(p.dovolenky || []).length > 0 && <span style={{ color: COLORS.nav, fontSize: TYPO.sizes.xs }}>dovolenka {p.dovolenky.map(v => fmtD(v.from) + '–' + fmtD(v.to)).join(', ')}</span>}
            </button>
          ))}
        </div>
      )}

      {/* ── GANTT ── */}
      <div style={{ ...card, overflow: 'hidden' }}>
        {/* hlavička týždňov */}
        <div style={{ display: 'flex', borderBottom: `2px solid ${COLORS.line}`, background: COLORS.bgSoft }}>
          <div style={{ width: 220, flexShrink: 0, padding: `${SPACE.sm}px ${SPACE.md}px`, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>Poschodie</div>
          <div style={{ flex: 1, position: 'relative', minHeight: 30 }}>
            {D.weeks.map(w => (
              <div key={w} style={{ position: 'absolute', left: D.pos(w) + '%', top: 0, bottom: 0, borderLeft: `1px solid ${COLORS.line}`, paddingLeft: 4, fontSize: TYPO.sizes.xs, color: COLORS.muted, display: 'flex', alignItems: 'center' }}>{fmtD(w)}</div>
            ))}
            {deadlinePos != null && <div style={{ position: 'absolute', left: deadlinePos + '%', top: 2, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.remove, transform: 'translateX(-50%)' }}>TERMÍN</div>}
          </div>
        </div>

        {Object.entries(D.objs).map(([obj, rows]) => (
          <div key={obj}>
            <div style={{ padding: `${SPACE.sm}px ${SPACE.md}px`, background: COLORS.navBg, fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold, color: COLORS.nav, borderBottom: `1px solid ${COLORS.line}` }}>
              OBJEKT {obj} — Σ {fmtM2(rows.reduce((s, f) => s + f.m2, 0))} m²
            </div>
            {rows.map(f => {
              const ps = (f.cfg.parties || []).map(id => parties.find(p => p.id === id)).filter(Boolean)
              const barBg = ps.length > 1
                ? `linear-gradient(90deg, ${ps.map((p, i) => `${p.color} ${i / ps.length * 100}%, ${p.color} ${(i + 1) / ps.length * 100}%`).join(', ')})`
                : (ps[0]?.color || COLORS.disabled)
              const inProgress = f.sim && f.cfg.start <= D.today && f.sim.end >= D.today
              return (
                <div key={f.key}
                  style={{ display: 'flex', borderBottom: `1px solid ${COLORS.lineSoft}`, background: COLORS.bg }}
                  onMouseEnter={e => { e.currentTarget.style.background = COLORS.hover }}
                  onMouseLeave={e => { e.currentTarget.style.background = COLORS.bg }}>
                  <div onClick={() => canEdit && setEditFloor(f)} title="Detail poschodia (čiastočné m², spätná história)"
                    style={{ width: 220, flexShrink: 0, padding: `${SPACE.sm}px ${SPACE.md}px`, display: 'flex', alignItems: 'center', gap: 8, cursor: canEdit ? 'pointer' : 'default' }}>
                    <b style={{ fontSize: TYPO.sizes.base, color: COLORS.text, width: 42 }}>{f.po}</b>
                    <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted }}>{fmtM2(f.m2)} m² · {f.byty} b.</span>
                    {f.cfg.done && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.add, fontWeight: TYPO.weights.bold }}>✓</span>}
                    {!f.cfg.done && f.doneM2 > 0 && <span style={{ fontSize: TYPO.sizes.xs, color: COLORS.add }}>{Math.round(f.doneM2 / f.m2 * 100)} %</span>}
                  </div>
                  <div style={{ flex: 1, position: 'relative', minHeight: 38, cursor: canEdit ? 'pointer' : 'default' }}
                    onClick={e => {
                      if (!canEdit) return
                      const rect = e.currentTarget.getBoundingClientRect()
                      const frac = (e.clientX - rect.left) / rect.width
                      const clicked = addDays(D.min, Math.floor(frac * D.span))
                      setPopover({ key: f.key, obj: f.obj, po: f.po, m2: f.m2, week: nextWork(mondayOf(clicked)), x: e.clientX, y: e.clientY })
                    }}>
                    {D.weeks.map(w => <div key={w} style={{ position: 'absolute', left: D.pos(w) + '%', top: 0, bottom: 0, borderLeft: `1px solid ${COLORS.lineSoft}` }} />)}
                    {todayPos != null && <div style={{ position: 'absolute', left: todayPos + '%', top: 0, bottom: 0, borderLeft: `1px dashed ${COLORS.nav}`, opacity: 0.5 }} />}
                    {deadlinePos != null && <div style={{ position: 'absolute', left: deadlinePos + '%', top: 0, bottom: 0, borderLeft: `2px solid ${COLORS.remove}` }} />}
                    {f.cfg.done ? (
                      f.cfg.doneFrom && f.cfg.doneTo && f.cfg.doneTo >= f.cfg.doneFrom ? (
                        <div title={`Hotové · ${fmtD(f.cfg.doneFrom)} – ${fmtD(f.cfg.doneTo)}${ps.length ? ' · ' + ps.map(p => p.name).join(' + ') : ''}`}
                          style={{ position: 'absolute', left: D.pos(f.cfg.doneFrom) + '%', width: Math.max(1.5, D.pos(addDays(f.cfg.doneTo, 1)) - D.pos(f.cfg.doneFrom)) + '%', top: 7, height: 24, background: COLORS.addBg, border: `1px solid ${COLORS.add}`, borderRadius: RADIUS.md, display: 'flex', alignItems: 'center', paddingLeft: 8, fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.add, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                          Hotové{ps.length ? ' · ' + ps.map(p => p.name).join(' + ') : ''}
                        </div>
                      ) : (
                        <div style={{ position: 'absolute', left: '0.5%', top: 7, height: 24, width: '20%', background: COLORS.addBg, border: `1px solid ${COLORS.add}`, borderRadius: RADIUS.md, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.add }}>Hotové</div>
                      )
                    ) : f.sim ? (
                      <>
                        {ps.length === 0 && (
                          <div title={`${fmtD(f.cfg.start || f.sim.end)} – ${fmtD(f.sim.end)} · dátumy ručne`}
                            style={{ position: 'absolute', left: D.pos(f.cfg.start || f.sim.end) + '%', width: Math.max(1.5, D.pos(addDays(f.sim.end, 1)) - D.pos(f.cfg.start || f.sim.end)) + '%', top: 7, height: 24, background: COLORS.disabled, borderRadius: RADIUS.md, display: 'flex', alignItems: 'center', paddingLeft: 8, fontSize: TYPO.sizes.xs, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                            {fmtD(f.cfg.start || f.sim.end)}–{fmtD(f.sim.end)}
                          </div>
                        )}
                        {ps.map((p, i) => {
                          const seg = segOf(f, p)
                          if (!seg) return null
                          const n = ps.length
                          const h = n > 1 ? Math.max(9, Math.floor(26 / n) - 2) : 24
                          const top = n > 1 ? 4 + i * (h + 2) : 7
                          return (
                            <div key={p.id} title={`${p.name} · ${fmtD(seg.st)} – ${fmtD(seg.en)}${seg.manual ? ' · dátumy ručne' : ''}`}
                              style={{ position: 'absolute', left: D.pos(seg.st) + '%', width: Math.max(1.5, D.pos(addDays(seg.en, 1)) - D.pos(seg.st)) + '%', top, height: h, background: p.color, borderRadius: 4, display: 'flex', alignItems: 'center', paddingLeft: 6, fontSize: n > 1 ? 9 : TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }}>
                              {p.name} · {fmtD(seg.st)}–{fmtD(seg.en)}{n === 1 && inProgress ? ' · prebieha' : ''}
                            </div>
                          )
                        })}
                        {ranges(f.sim.vacDays).map(r => (
                          <div key={r.from} title={`dovolenka ${fmtD(r.from)} – ${fmtD(r.to)}`}
                            style={{ position: 'absolute', left: D.pos(r.from) + '%', width: Math.max(0.8, D.pos(addDays(r.to, 1)) - D.pos(r.from)) + '%', top: 4, height: 30, borderRadius: 3, background: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.75), rgba(255,255,255,0.75) 4px, transparent 4px, transparent 8px)' }} />
                        ))}
                      </>
                    ) : (
                      <div style={{ position: 'absolute', left: '0.5%', top: 11, fontSize: TYPO.sizes.xs, color: COLORS.disabled }}>nepriradené — klikni</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ))}
        {D.skipped.length > 0 && (
          <div style={{ padding: `${SPACE.sm}px ${SPACE.md}px`, fontSize: TYPO.sizes.xs, color: COLORS.muted, background: COLORS.bgSoft }}>
            Neráta sa (0 m² vo Výmerách): {D.skipped.map(f => `${f.obj} ${f.po}`).join(', ')}
          </div>
        )}
      </div>

      {/* ── popover: klik do mriežky (ako Rozpis) ── */}
      {popover && (() => {
        const key = popover.key
        const cfg = (plan.floors || {})[key] || {}
        const left = Math.max(8, Math.min(popover.x - 140, (window.innerWidth || 1200) - 300))
        const top = Math.min(popover.y + 10, (window.innerHeight || 700) - 320)
        return (
          <>
            <div onClick={() => setPopover(null)} style={{ position: 'fixed', inset: 0, zIndex: 998 }} />
            <div style={{ position: 'fixed', left, top, zIndex: 999, width: 290, background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, boxShadow: '0 10px 30px rgba(0,0,0,0.2)', padding: SPACE.md }}>
              <div style={{ fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold, color: COLORS.text, marginBottom: 2 }}>
                Objekt {popover.obj} — {popover.po}
              </div>
              <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: SPACE.sm }}>
                týždeň od {fmtD(popover.week)} · {fmtM2(popover.m2)} m²
              </div>
              {(() => {
                const rows = (aktivita || []).filter(a => a.obj === popover.obj && a.po === popover.po && a.m2 > 0)
                return rows.length ? (
                  <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.nav, background: COLORS.navBg, borderRadius: RADIUS.md, padding: '5px 8px', marginBottom: SPACE.sm }}>
                    Vo Výmerách tu pracuje: {rows.map(a => `${a.partak} (${a.byty} b.${a.done_byty ? `, ${a.done_byty} ✓` : ''})`).join(' · ')}
                  </div>
                ) : null
              })()}
              {parties.length === 0 ? (
                <div style={hintStyle}>Najprv pridaj partie tlačidlom „+ Partia".</div>
              ) : (
                <div style={{ display: 'flex', gap: SPACE.xs, flexWrap: 'wrap', marginBottom: SPACE.sm }}>
                  {parties.map(p => {
                    const on = (cfg.parties || []).includes(p.id)
                    const cudzi = p.objekt && p.objekt !== popover.obj
                    return (
                      <button key={p.id} title={cudzi ? `Partia je priradená objektu ${p.objekt}` : ''}
                        onClick={() => {
                          const next = on ? (cfg.parties || []).filter(x => x !== p.id) : [...(cfg.parties || []), p.id]
                          setFloorCfg(key, { parties: next, start: cfg.start || popover.week })
                        }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 999, cursor: 'pointer', fontSize: TYPO.sizes.sm, fontFamily: TYPO.family, border: `2px solid ${on ? p.color : COLORS.line}`, background: on ? p.color : COLORS.bg, color: on ? '#fff' : (cudzi ? COLORS.disabled : COLORS.text), fontWeight: on ? TYPO.weights.bold : TYPO.weights.normal }}>
                        {p.name}
                      </button>
                    )
                  })}
                </div>
              )}
              <div style={{ display: 'flex', gap: SPACE.xs, flexWrap: 'wrap', borderTop: `1px solid ${COLORS.lineSoft}`, paddingTop: SPACE.sm }}>
                {cfg.start && cfg.start !== popover.week && (cfg.parties || []).length > 0 && !cfg.done && (
                  <Btn variant="nav" size="sm" onClick={() => { setFloorCfg(key, { start: popover.week }); setPopover(null) }}>Presunúť štart sem</Btn>
                )}
                <Btn variant={cfg.done ? 'ghost' : 'add'} size="sm" onClick={() => { setFloorCfg(key, { done: !cfg.done }); setPopover(null) }}>{cfg.done ? 'Zrušiť Hotové' : 'Hotové'}</Btn>
                <BtnGhost size="sm" onClick={() => { setPopover(null); setEditFloor(D.rows.find(r => r.key === key)) }}>Detail…</BtnGhost>
                {(cfg.parties || []).length > 0 || cfg.done || cfg.start ? (
                  <BtnGhost size="sm" onClick={() => { clearFloor(key); setPopover(null) }}>Vyčistiť</BtnGhost>
                ) : null}
              </div>
            </div>
          </>
        )
      })()}

      {/* ── modal: poschodie ── */}
      <Modal open={!!editFloor} onClose={() => setEditFloor(null)} title={editFloor ? `Objekt ${editFloor.obj} — ${editFloor.po}` : ''} subtitle={editFloor ? `${fmtM2(editFloor.m2)} m² z Výmer · ${editFloor.byty} bytov` : ''} width="min(560px, 95vw)">
        {editFloor && (() => {
          const key = editFloor.key
          const cfg = (plan.floors || {})[key] || {}
          const aktRows = (aktivita || []).filter(a => a.obj === editFloor.obj && a.po === editFloor.po && a.m2 > 0)
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.lg, padding: SPACE.sm }}>
              {aktRows.length > 0 && (
                <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.nav, background: COLORS.navBg, borderRadius: RADIUS.md, padding: '7px 10px' }}>
                  Vo Výmerách tu pracuje: {aktRows.map(a => `${a.partak} (${a.byty} b., ${fmtM2(a.m2)} m²${a.done_byty ? `, ${a.done_byty} hotových` : ''})`).join(' · ')}
                </div>
              )}
              <div>
                <span style={lbl}>Stav</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: TYPO.sizes.base, color: COLORS.text, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!cfg.done} onChange={e => setFloorCfg(key, { done: e.target.checked })} />
                  Poschodie je HOTOVÉ (vyomietané)
                </label>
                {cfg.done && (
                  <div style={{ marginTop: SPACE.sm, display: 'flex', gap: SPACE.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div>
                      <span style={lbl}>Robilo sa od</span>
                      <input type="date" value={cfg.doneFrom || ''} onChange={e => setFloorCfg(key, { doneFrom: e.target.value })} style={inputStyle} />
                    </div>
                    <div>
                      <span style={lbl}>do</span>
                      <input type="date" value={cfg.doneTo || ''} onChange={e => setFloorCfg(key, { doneTo: e.target.value })} style={inputStyle} />
                    </div>
                    <div style={{ ...hintStyle, flexBasis: '100%' }}>Spätné doplnenie histórie — zelený pruh sa zobrazí na týchto dátumoch. Nepovinné (bez dátumov je pill vľavo).</div>
                  </div>
                )}
                {!cfg.done && (
                  <div style={{ marginTop: SPACE.sm }}>
                    <span style={lbl}>Čiastočne hotové (m²)</span>
                    <input type="number" min="0" max={editFloor.m2} step="1" value={cfg.doneM2 || ''} placeholder="0"
                      onChange={e => setFloorCfg(key, { doneM2: e.target.value === '' ? 0 : Number(e.target.value) })} style={{ ...inputStyle, width: 140 }} />
                    <div style={hintStyle}>Ak je časť poschodia už vyomietaná (napr. 2 byty), zadaj hotové m² — odráta sa zo zostatku aj z trvania.</div>
                  </div>
                )}
              </div>
              <div>
                    <span style={lbl}>{cfg.done ? 'Kto to robil (partie)' : 'Partie na poschodí'}</span>
                    {parties.length === 0 ? <div style={hintStyle}>Najprv pridaj partiu tlačidlom „+ Partia" hore.</div> : (
                      <div style={{ display: 'flex', gap: SPACE.xs, flexWrap: 'wrap' }}>
                        {parties.map(p => {
                          const on = (cfg.parties || []).includes(p.id)
                          return (
                            <button key={p.id} onClick={() => setFloorCfg(key, { parties: on ? (cfg.parties || []).filter(x => x !== p.id) : [...(cfg.parties || []), p.id] })}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, cursor: 'pointer', fontSize: TYPO.sizes.sm, fontFamily: TYPO.family, border: `2px solid ${on ? p.color : COLORS.line}`, background: on ? p.color : COLORS.bg, color: on ? '#fff' : COLORS.text, fontWeight: on ? TYPO.weights.bold : TYPO.weights.normal }}>
                              {p.name}
                            </button>
                          )
                        })}
                      </div>
                    )}
                    <div style={hintStyle}>Môžeš vybrať aj dve partie spolu — výkon sa sčíta (veríme výkonu partií).</div>
                    {!cfg.done && (cfg.parties || []).length >= 2 && (
                      <div style={{ marginTop: SPACE.sm, display: 'flex', flexDirection: 'column', gap: SPACE.xs }}>
                        {(cfg.parties || []).map(id => {
                          const p = parties.find(x => x.id === id)
                          if (!p) return null
                          const o = (cfg.pp || {})[id] || {}
                          const setPP = patch => setFloorCfg(key, { pp: { ...(cfg.pp || {}), [id]: { ...o, ...patch } } })
                          return (
                            <div key={id} style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, flexWrap: 'wrap' }}>
                              <span style={{ minWidth: 130, fontSize: TYPO.sizes.sm, color: COLORS.text }}>
                                <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: p.color, marginRight: 6 }} />{p.name}
                              </span>
                              <input type="date" value={o.start || ''} onChange={e => setPP({ start: e.target.value })} style={inputStyle} />
                              <span style={{ color: COLORS.muted }}>–</span>
                              <input type="date" value={o.end || ''} onChange={e => setPP({ end: e.target.value })} style={inputStyle} />
                              {(o.start || o.end) && <BtnGhost size="sm" onClick={() => setPP({ start: '', end: '' })}>×</BtnGhost>}
                            </div>
                          )
                        })}
                        <div style={hintStyle}>VLASTNÝ začiatok a koniec každej partie na poschodí — každá má svoj pruh. Prázdne = spoločný začiatok poschodia / výpočet z výkonu.</div>
                      </div>
                    )}
              </div>
              {!cfg.done && (
                  <div>
                    <div style={{ display: 'flex', gap: SPACE.lg, flexWrap: 'wrap' }}>
                      <div>
                        <span style={lbl}>Začiatok prác</span>
                        <input type="date" value={cfg.start || ''} onChange={e => setFloorCfg(key, { start: e.target.value })} style={inputStyle} />
                      </div>
                      <div>
                        <span style={lbl}>Koniec — ručne</span>
                        <input type="date" value={cfg.end || ''} onChange={e => setFloorCfg(key, { end: e.target.value })} style={inputStyle} />
                        {cfg.end && <BtnGhost size="sm" onClick={() => setFloorCfg(key, { end: '' })} style={{ marginLeft: 6 }}>× zrušiť</BtnGhost>}
                      </div>
                    </div>
                    <div style={hintStyle}>Ak zadáš ručný koniec, pruh končí TÝMTO dátumom a výpočet z výkonu sa pre toto poschodie NEPOUŽIJE (žiadne „somariny"). Zmaž koniec a výpočet sa vráti.</div>
                  </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}><Btn onClick={() => setEditFloor(null)}>Zavrieť</Btn></div>
            </div>
          )
        })()}
      </Modal>

      {/* ── modal: partia ── */}
      <Modal open={!!editParty} onClose={() => setEditParty(null)} title={editParty?.name ? 'Partia — ' + editParty.name : 'Nová partia'} width="min(560px, 95vw)">
        {editParty && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.lg, padding: SPACE.sm }}>
            <div>
              <span style={lbl}>Názov partie / parťák</span>
              {(() => {
                const used = parties.filter(x => x.id !== editParty.id).map(x => (x.name || '').toLowerCase().trim())
                const free = rozpis.filter(r => !used.includes((r.name || '').toLowerCase().trim()))
                return free.length > 0 && (
                  <div style={{ display: 'flex', gap: SPACE.xs, flexWrap: 'wrap', marginBottom: SPACE.sm }}>
                    {free.map(r => (
                      <button key={r.name} onClick={() => setEditParty({ ...editParty, name: r.name })}
                        style={{ padding: '4px 10px', borderRadius: 999, cursor: 'pointer', fontSize: TYPO.sizes.sm, fontFamily: TYPO.family, border: `1px dashed ${editParty.name === r.name ? COLORS.nav : COLORS.line}`, background: editParty.name === r.name ? COLORS.navBg : COLORS.bgSoft, color: COLORS.text }}>
                        {r.name}{r.is_partak ? '' : ' (člen)'}
                      </button>
                    ))}
                  </div>
                )
              })()}
              <input value={editParty.name} autoFocus onChange={e => setEditParty({ ...editParty, name: e.target.value })} style={{ ...inputStyle, width: '100%' }} placeholder="napr. Slavo Mirga" />
              <div style={hintStyle}>Mená sa ponúkajú z Rozpisu prác tejto zákazky (vlákno) — klikni na meno, alebo napíš vlastné. Ak niekto chýba, pridaj ho najprv do Rozpisu prác.</div>
            </div>
            <div style={{ display: 'flex', gap: SPACE.lg, flexWrap: 'wrap' }}>
              <div>
                <span style={lbl}>Výkon (m² / týždeň)</span>
                <input type="number" min="1" step="10" value={editParty.vykon}
                  onChange={e => setEditParty({ ...editParty, vykon: Number(e.target.value) })} style={{ ...inputStyle, width: 120 }} />
                <div style={hintStyle}>Reálny výkon TEJTO partie za 5 prac. dní — veríme výkonu. Každá partia robí inak.</div>
              </div>
              <div>
                <span style={lbl}>Víkend</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: TYPO.sizes.base, color: COLORS.text, cursor: 'pointer', marginTop: 6 }}>
                  <input type="checkbox" checked={!!editParty.weekend} onChange={e => setEditParty({ ...editParty, weekend: e.target.checked })} />
                  robí aj víkendy
                </label>
              </div>
              <div>
                <span style={lbl}>Sólo</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: TYPO.sizes.base, color: COLORS.text, cursor: 'pointer', marginTop: 6 }}>
                  <input type="checkbox" checked={!!editParty.solo} onChange={e => setEditParty({ ...editParty, solo: e.target.checked })} />
                  robí poschodie sám
                </label>
                <div style={hintStyle}>Auto-naplánovať dáva partie na poschodia ZDVOJENE (2 spolu). Sólo partia dostáva poschodia sama.</div>
              </div>
            </div>
            <div>
              <span style={lbl}>Objekt</span>
              <div style={{ display: 'flex', gap: SPACE.xs, flexWrap: 'wrap' }}>
                {['', ...new Set((floors || []).filter(f => f.m2 > 0).map(f => f.obj))].map(o => (
                  <button key={o || 'vsetky'} onClick={() => setEditParty({ ...editParty, objekt: o })}
                    style={{ padding: '5px 12px', borderRadius: 999, cursor: 'pointer', fontSize: TYPO.sizes.sm, fontFamily: TYPO.family, border: `2px solid ${(editParty.objekt || '') === o ? COLORS.nav : COLORS.line}`, background: (editParty.objekt || '') === o ? COLORS.navBg : COLORS.bg, color: COLORS.text, fontWeight: (editParty.objekt || '') === o ? TYPO.weights.bold : TYPO.weights.normal }}>
                    {o || 'Všetky'}
                  </button>
                ))}
              </div>
              <div style={hintStyle}>Na ktorom objekte partia robí — Auto-naplánovať jej dáva len poschodia jej objektu. „Všetky" = môže hocikde.</div>
            </div>
            <div>
              <span style={lbl}>Farba</span>
              <div style={{ display: 'flex', gap: SPACE.xs }}>
                {PALETTE.map(c => (
                  <button key={c} onClick={() => setEditParty({ ...editParty, color: c })}
                    style={{ width: 26, height: 26, borderRadius: '50%', background: c, cursor: 'pointer', border: editParty.color === c ? `3px solid ${COLORS.base}` : '3px solid transparent' }} />
                ))}
              </div>
            </div>
            <div>
              <span style={lbl}>Dovolenky / nedostupnosť</span>
              {(editParty.dovolenky || []).map((v, i) => (
                <div key={i} style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', marginBottom: SPACE.xs }}>
                  <input type="date" value={v.from || ''} onChange={e => { const dv = [...editParty.dovolenky]; dv[i] = { ...dv[i], from: e.target.value }; setEditParty({ ...editParty, dovolenky: dv }) }} style={inputStyle} />
                  <span style={{ color: COLORS.muted }}>–</span>
                  <input type="date" value={v.to || ''} onChange={e => { const dv = [...editParty.dovolenky]; dv[i] = { ...dv[i], to: e.target.value }; setEditParty({ ...editParty, dovolenky: dv }) }} style={inputStyle} />
                  <BtnGhost onClick={() => setEditParty({ ...editParty, dovolenky: editParty.dovolenky.filter((_, j) => j !== i) })}>×</BtnGhost>
                </div>
              ))}
              <BtnGhost onClick={() => setEditParty({ ...editParty, dovolenky: [...(editParty.dovolenky || []), { from: '', to: '' }] })}>+ Pridať dovolenku</BtnGhost>
              <div style={hintStyle}>Počas dovolenky partia nerobí — v Gantte sa zobrazí šrafovaná medzera a dokončenie sa posunie.</div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: SPACE.sm }}>
              {parties.some(x => x.id === editParty.id)
                ? <BtnDanger onClick={() => { if (window.confirm('Odstrániť partiu z harmonogramu? Zmizne aj z priradených poschodí.')) removeParty(editParty.id) }}>Odstrániť</BtnDanger>
                : <span />}
              <div style={{ display: 'flex', gap: SPACE.sm }}>
                <BtnGhost onClick={() => setEditParty(null)}>Zrušiť</BtnGhost>
                <BtnAdd onClick={() => { if (!editParty.name.trim()) { toast.warn('Zadaj názov partie.'); return } saveParty(editParty) }}>Uložiť partiu</BtnAdd>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

// ── Harmonogram po remeslách (SPEC harmonogram v1, 2.3): prepínač remesla a režimu, každý v samostatnom ErrorBoundary ──
const REZIMY = [{ key: 'vykon', label: 'Výkon m²/týždeň' }, { key: 'harok', label: 'Úseky (hárok)' }]
const citajRemeslo = (pid) => {
  try { const r = sessionStorage.getItem('matrix:harm:remeslo:' + pid); return REMESLA.some(x => x.key === r) ? r : 'omietky' } catch (_) { return 'omietky' }
}

export default function HarmonogramTab({ projectId }) {
  const toast = useToast()
  const { user } = useAuth()
  const canEdit = WRITE_ROLES.includes(user?.role)
  const [volba, setVolba] = useState(() => ({ pid: projectId, remeslo: citajRemeslo(projectId) }))
  const [info, setInfo] = useState(null)          // {pid, ok, list} z /remesla
  const [lokalRezim, setLokalRezim] = useState({}) // prepnutie len na obrazovke pre rolu bez zápisu
  const [rezimBusy, setRezimBusy] = useState(false)
  const [pokus, setPokus] = useState(0)

  useEffect(() => {
    let alive = true
    fetchAuthJSON(`/api/harmonogram/${projectId}/remesla`)
      .then(list => { if (alive) setInfo({ pid: projectId, ok: Array.isArray(list), list: Array.isArray(list) ? list : [] }) })
      .catch(() => { if (alive) setInfo({ pid: projectId, ok: false, list: [] }) })
    return () => { alive = false }
  }, [projectId, pokus])

  // režim remesla môže prepnúť iný používateľ (globálna poistka useLiveRefresh chráni rozpísané zmeny)
  useLiveRefresh(() => {
    const pid = projectId
    fetchAuthJSON(`/api/harmonogram/${pid}/remesla`)
      .then(list => { if (Array.isArray(list)) setInfo(i => (i && i.pid === pid && i.ok ? { ...i, list } : i)) })
      .catch(() => {})
  }, () => !rezimBusy)

  if (!info || info.pid !== projectId) return <div style={{ padding: SPACE.xl, color: COLORS.muted }}>Načítavam harmonogram…</div>

  // bez /remesla (starý server) ostáva len pôvodný harmonogram omietok
  const ok = info.ok
  const remeslo = ok ? (volba.pid === projectId ? volba.remeslo : citajRemeslo(projectId)) : 'omietky'
  const riadok = info.list.find(x => x.remeslo === remeslo) || {}
  const rezim = ok ? (lokalRezim[projectId + '|' + remeslo] || riadok.rezim || (remeslo === 'omietky' ? 'vykon' : 'harok')) : 'vykon'

  const zmenRemeslo = (r) => {
    if (r === remeslo) return
    confirmLeave().then(go => {
      if (!go) return
      try { sessionStorage.setItem('matrix:harm:remeslo:' + projectId, r) } catch (_) {}
      setVolba({ pid: projectId, remeslo: r })
    })
  }
  const zmenRezim = (z) => {
    if (z === rezim || rezimBusy) return
    confirmLeave().then(async go => {
      if (!go) return
      const k = projectId + '|' + remeslo
      if (!canEdit) { setLokalRezim(m => ({ ...m, [k]: z })); return }
      setRezimBusy(true)
      try {
        const r = await fetchAuth(`/api/harmonogram/${projectId}/rezim`, { method: 'PATCH', body: JSON.stringify({ remeslo, rezim: z }) })
        if (!r.ok) {
          let det = ''
          try { const j = await r.json(); det = j.error || '' } catch (_) {}
          throw new Error('HTTP ' + r.status + (det ? ': ' + det : ''))
        }
        setInfo(i => (i && i.pid === projectId ? { ...i, list: REMESLA.map(x => { const row = i.list.find(y => y.remeslo === x.key) || { remeslo: x.key }; return x.key === remeslo ? { ...row, rezim: z } : row }) } : i))
        setLokalRezim(m => { const n = { ...m }; delete n[k]; return n })
      } catch (e) { toast.error('Režim harmonogramu sa nepodarilo prepnúť: ' + e.message) }
      finally { setRezimBusy(false) }
    })
  }

  const remLabel = (REMESLA.find(x => x.key === remeslo) || {}).label || remeslo
  const rezLabel = (REZIMY.find(x => x.key === rezim) || {}).label || rezim
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {!ok && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: SPACE.md, flexWrap: 'wrap', padding: '6px 12px', marginBottom: SPACE.sm, background: COLORS.totalBg, border: `1px solid ${COLORS.total}`, borderRadius: RADIUS.md, color: COLORS.total, fontSize: TYPO.sizes.sm }}>
          <span style={{ flex: 1 }}>Remeslá sa nenačítali — zobrazuje sa harmonogram omietok. Maľby a potery budú dostupné po načítaní.</span>
          <Btn size="sm" variant="nav" onClick={() => setPokus(p => p + 1)}>Skúsiť znova</Btn>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: SPACE.lg, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <Tabs tabs={REMESLA.map(r => ({ key: r.key, label: r.label, active: ok || r.key === 'omietky' }))} value={remeslo} onChange={zmenRemeslo} />
        </div>
        {ok && (
          <div role="group" aria-label="Režim harmonogramu" style={{ display: 'flex', gap: SPACE.xs, marginBottom: SPACE.md }}>
            {REZIMY.map(z => (
              <Btn key={z.key} size="sm" variant={rezim === z.key ? 'nav' : 'ghost'} aria-pressed={rezim === z.key} disabled={rezimBusy}
                title={z.key === 'vykon' ? 'Plán z m² Výmer a výkonu partií' : 'Úseky ako v Exceli: partia, úsek, začiatok, dni'}
                onClick={() => zmenRezim(z.key)}>{z.label}</Btn>
            ))}
          </div>
        )}
      </div>
      <ErrorBoundary key={projectId + '|' + remeslo + '|' + rezim} label={`Harmonogram · ${remLabel} · ${rezLabel}`}>
        {rezim === 'harok'
          ? <HarmonogramHarok projectId={projectId} remeslo={remeslo} />
          : <HarmonogramVykon projectId={projectId} remeslo={remeslo} />}
      </ErrorBoundary>
    </div>
  )
}
